import { BpButton } from '../../../../shared/components/buttons/bp-button/BpButton'
import { Square } from '../../../../shared/components/utility/square/Square'
import { BpArticle, IBpArticleProps } from '../../../../shared/components/cards/article/BpArticle'
import React, { useState } from 'react'
import { IBody, usePoolRequest } from '../../../../shared/lib/hooks/UsePoolRequest'
import { useAppDispatch, useAppSelector } from '../../../../app/store/hooks/Redux'
import { selectLiveSearchFilters, selectLiveSearchType } from '../../model/liveSearchSlice'
import { Preloader } from '../../../../shared/components/cards/preloader/Preloader'
import {
  useLazyGetResearchLiveDecloakAutoQuery,
  useLazyGetResearchLiveRedirectsQuery,
} from '../../../../app/api'
import { LinksList } from '../../../ad-overview/widgets/link-list/LinksList'
import { showAlert } from '../../../../app/store/reducers/AppSlice'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import styles from './LiveSearchContent.module.scss'

const Row = ({
  isSeoLink,
  counter,
  allowDecloak,
  ...props
}: {
  isSeoLink: boolean
  href: string
  counter: number
  allowDecloak: boolean
} & IBpArticleProps) => {
  const { t } = useTranslation()
  const [linksOpen, setLinksOpen] = useState(false)
  const filters = useAppSelector(selectLiveSearchFilters)
  const dispatch = useAppDispatch()

  const [getLiveRedirects, { isLoading, isFetching, isUninitialized }] =
    useLazyGetResearchLiveRedirectsQuery({
      refetchOnFocus: false,
    })
  const {
    start,
    data,
    isLoading: poolLoading,
    isUninitialized: poolUninitialized,
    isTimeout: poolTimeout,
  } = usePoolRequest({
    request: useLazyGetResearchLiveRedirectsQuery,
  })
  const [getLiveDecloakAuto, { isUninitialized: decloadIsUninitialized }] =
    useLazyGetResearchLiveDecloakAutoQuery()

  return (
    <Square className={clsx(styles.row)} depth='200'>
      <div className={clsx(styles.counterBlock)}>
        <span className={clsx(styles.counterLabel)}>#{counter}</span>
        <div className={clsx(styles.badge, isSeoLink ? styles.badgeOrganic : styles.badgePaid)}>
          {isSeoLink ? t('pages.liveSearch.organic') : t('pages.liveSearch.paid')}
        </div>
      </div>
      <div className={clsx(styles.content)}>
        <div className={clsx(styles.articleWrapper)}>
          <BpArticle href={props.href} link={props.link} text={props.text} title={props.title} />
        </div>
        <div className={clsx(styles.linksRow)}>
          <span
            className={clsx(styles.toggleLinks)}
            onClick={() => {
              setLinksOpen(!linksOpen)
              isUninitialized &&
                getLiveRedirects({
                  engineId: filters.searchEngine as number,
                  countryId: filters.country as number,
                  deviceTypeId: filters.device as number,
                  isSeoLink: isSeoLink,
                  linkUrl: props.href as string,
                  linkTitle: props.title as string,
                  linkText: props.text as string,
                  linkDomain: props.link as string,
                  keyword: filters.keyword,
                }).then((response) => {
                  start(response?.data as unknown as IBody | undefined)
                })
            }}
          >
            {linksOpen ? t('buttons.closeRedirectList') : t('buttons.SERPRedirectLinks')}
          </span>
          {allowDecloak && !isSeoLink && (
            <BpButton
              disabled={!decloadIsUninitialized}
              size='sm'
              onClick={() => {
                getLiveDecloakAuto({
                  engineId: filters.searchEngine as number,
                  countryId: filters.country as number,
                  deviceTypeId: filters.device as number,
                  isSeoLink: isSeoLink,
                  linkUrl: props.href as string,
                  linkTitle: props.title as string,
                  linkText: props.text as string,
                  linkDomain: props.link as string,
                  keyword: filters.keyword,
                })
                dispatch(
                  showAlert({
                    message: t('pages.liveSearch.uncloakingProcessMayTakeSomeTime'),
                    type: 'success',
                  }),
                )
              }}
            >
              <span className={clsx(styles.buttonText)}>
                {decloadIsUninitialized
                  ? t('buttons.startUncloaking')
                  : t('buttons.uncloakingInProgress')}
              </span>
            </BpButton>
          )}
        </div>
        <div className={clsx(styles.linksListWrapper, !linksOpen && styles.linksListWrapperHidden)}>
          {!isUninitialized && (
            <LinksList
              className={clsx(styles.linksListNoShadow)}
              isLoading={(poolLoading && !poolTimeout) || isLoading || isFetching}
              redirects={data?.data?.redirects as string[]}
            />
          )}
        </div>
      </div>
    </Square>
  )
}

export const LiveSearchContent = ({
  data,
  isLoading,
  isUninitialized,
}: {
  data?: any
  isLoading: boolean
  isUninitialized: boolean
}) => {
  const { t } = useTranslation()
  const type = useAppSelector(selectLiveSearchType)

  if (isLoading) {
    return (
      <div className={clsx(styles.preloaderWrapper)}>
        <Preloader />
      </div>
    )
  }

  return data?.items?.length ? (
    <div className={clsx(styles.list)}>
      {data.items
        .filter((row: any) => {
          switch (type) {
            case 'all':
              return true
            case 'organic':
              return row.isSeoLink
            case 'paid':
              return !row.isSeoLink
          }
        })
        .map((row: any) => (
          <Row
            key={row.position}
            allowDecloak={data.allow_decloak}
            counter={row.position}
            href={row.link}
            isSeoLink={row.isSeoLink}
            link={row.domain}
            text={row.description}
            title={row.title}
          />
        ))}
    </div>
  ) : (
    <div className={clsx(styles.emptyState)}>
      <svg
        fill='none'
        height='85'
        viewBox='0 0 78 85'
        width='78'
        xmlns='http://www.w3.org/2000/svg'
      >
        <path
          d='M30 77.0004L35.892 80.2736C37.0264 80.904 37.5936 81.2188 38.1944 81.3424C38.726 81.452 39.274 81.452 39.806 81.3424C40.4064 81.2188 40.9736 80.904 42.108 80.2736L48 77.0004M12 67.0004L6.29188 63.8292C5.09384 63.1636 4.49472 62.8308 4.05852 62.3572C3.67264 61.9388 3.3806 61.4424 3.20192 60.9016C3 60.2904 3 59.6052 3 58.2344V52.0004M3 32.0004V25.7661C3 24.3956 3 23.7103 3.20192 23.0991C3.3806 22.5584 3.67264 22.062 4.05852 21.6433C4.49472 21.1699 5.0938 20.8371 6.29188 20.1715L12 17.0004M30 7.00032L35.892 3.72708C37.0264 3.09684 37.5936 2.78172 38.1944 2.65816C38.726 2.54884 39.274 2.54884 39.806 2.65816C40.4064 2.78172 40.9736 3.09684 42.108 3.72708L48 7.00032M66 17.0003L71.708 20.1715C72.9064 20.8371 73.5052 21.1699 73.9416 21.6433C74.3272 22.062 74.6196 22.5584 74.798 23.0991C75 23.7103 75 24.3956 75 25.7661V32.0003M75 52.0004V58.2344C75 59.6052 75 60.2904 74.798 60.9016C74.6196 61.4424 74.3272 61.9388 73.9416 62.3572C73.5052 62.8308 72.9064 63.1636 71.708 63.8292L66 67.0004M30 37.0004L39 42.0004M39 42.0004L48 37.0004M39 42.0004V52.0004M3 22.0003L12 27.0003M66 27.0003L75 22.0003M39 72.0004V82.0004'
          stroke='#6684A7'
          strokeLinecap='round'
          strokeLinejoin='round'
          strokeWidth='5'
        />
      </svg>
      {isUninitialized ? (
        <div className={clsx(styles.emptyStateText)}>
          <span className={clsx(styles.emptyStateTitle)}>
            {t('pages.liveSearch.selectDataToSearch')}
          </span>
          <span className={clsx(styles.emptyStateMessage)}>
            {t('pages.liveSearch.selectDataToSearchMessage')}
          </span>
        </div>
      ) : (
        <span className={clsx(styles.emptyStateTitle)}>{t('other.noResultsFound')}</span>
      )}
    </div>
  )
}
